import React from "react";
import { Helmet } from "react-helmet";
import { StaticImage } from "gatsby-plugin-image";
import { LinkWithUTM as Link } from "../components/LinkWithUTM";
import GreenCheckMark from "../components/icons/GreenCheckMark";
import LocationMark from "../components/icons/Locations/LocationMark";
import { MicroCombo } from "../components/Trustpilot/TrustpilotWidgets";
import { PostcodeCTA } from "../components/PostcodeCTA/PostcodeCTA";
import Trustpilot from "../components/Trustpilot/Trustpilot";
import Header from "../components/Header";
import Footer from "../components/Footer";

import "../../scss/main.scss";

export default function PartnerWithUs() {
  return (
    <>
      <Helmet>
        <meta charSet="UTF-8" />
        <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <title>Partner with us | edyn.care</title>
        <meta property="og:title" content="Partner with us | edyn.care" />
        <meta
          property="og:description"
          content="Help clients stay at home and create new revenue for your agency."
        />
      </Helmet>

      <div className="container">
        <Header />
        <main className="partner-london-page">
          <section>
            <div className="locations-landing">
              <div className="locations-landing-left">
                <div className="landing-content-container">
                  <div className="locations-heading">
                    <h1>Partner with edyn.care</h1>
                    <p className="primary">
                      Help clients stay at home - and create new revenue for your agency
                    </p>
                  </div>
                  <div className="landing-left-bottom">
                    <MicroCombo classNames={"trust-pilot-minimal partner-trust-pilot-mini"} />
                    <PostcodeCTA
                      title="Register your care agency today:"
                      ctaHref="#partner-typeform"
                      ctaLabel="Go to registration form"
                    />
                  </div>
                </div>
              </div>
              <div className="locations-landing-right partner-landing-right">
                <div className="partner-hero-image-wrap">
                  <StaticImage
                    src="../../static/assets/marketing-site/images/partner-with-us/hero.jpg"
                    alt="Older woman arranging flowers at home"
                    className="location-img"
                    objectFit="cover"
                    placeholder="blurred"
                  />
                </div>
                <div className="location-landing-name">
                  <LocationMark className="icon-default" />
                  <p>Dorothy, Brighton — staying at home thanks to her local care agency and edyn.care.</p>
                </div>
              </div>
            </div>
          </section>
          <hr className="mobile-hide" />

          <section>
            <div className="locations-we-understand">
              <div className="we-understand-left">
                <h2>Why Partner with edyn.care?</h2>
                <Link to="#partner-typeform" className="button-link">
                  <button className="btn-primary green">Register your interest</button>
                </Link>
              </div>
              <div className="we-understand-right">
                <ul>
                  <li className="mobile-only">
                    <h2>Why Partner with edyn.care?</h2>
                  </li>
                  <li>
                    <GreenCheckMark className="icon-default" />
                    <p className="secondary">Help clients avoid care homes</p>
                  </li>
                  <li>
                    <GreenCheckMark className="icon-default" />
                    <p className="secondary">Turn lost enquiries into revenue</p>
                  </li>
                  <li>
                    <GreenCheckMark className="icon-default" />
                    <p className="secondary">Increase client lifetime value</p>
                  </li>
                  <li>
                    <GreenCheckMark className="icon-default" />
                    <p className="secondary">A trusted live-in care partner</p>
                  </li>
                  <li className="mobile-only">
                    <Link to="#partner-typeform" className="button-link">
                      <button className="btn-primary green">Register your interest</button>
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          <section className="partner-exceptional-care-wrap">
            <div className="locations-exceptional-care partner-exceptional-care">
              <div className="ec-left">
                <h1>A partnership built to keep people at home</h1>
                <p className="primary">
                  Across the UK, thousands of people enter care homes every year simply because their
                  care needs increase beyond what hourly care services can provide.
                </p>
                <p className="primary">
                  But many families don&apos;t actually want a care home - they want to stay at home.
                  That&apos;s where live-in care becomes the ideal solution.
                </p>
                <p className="primary">
                  edyn.care partners with local hourly care agencies to ensure that when care needs
                  increase, clients can remain safely at home with live-in care - while your agency
                  continues to benefit from the relationship.
                </p>
                <p className="primary">
                  Many agencies continue supporting the client with double-up care, respite cover,
                  and additional visits while live-in care provides primary support.
                </p>
              </div>
              <div className="ec-right">
                <div className="ec-img-container ec-img-container-left">
                  <StaticImage
                    src="../../static/assets/marketing-site/images/partner-with-us/partnership-1.jpg"
                    alt="Care professional supporting a client at home"
                    objectFit="cover"
                    placeholder="blurred"
                  />
                  <StaticImage
                    src="../../static/assets/marketing-site/images/partner-with-us/partnership-2.jpg"
                    alt="Care support in a home setting"
                    objectFit="cover"
                    placeholder="blurred"
                  />
                </div>
                <div className="ec-img-container ec-img-container-right">
                  <StaticImage
                    src="../../static/assets/marketing-site/images/partner-with-us/partnership-3.jpg"
                    alt="Client and carer in a home environment"
                    objectFit="cover"
                    placeholder="blurred"
                  />
                </div>
              </div>
            </div>
          </section>

          <section className="partner-revenue-wrap">
            <div className="partner-revenue-section">
              <h2>Referral Income</h2>
              <p className="primary partner-revenue-intro">
                A simple referral model with one upfront fee and ongoing collaborative value where
                your team remains involved.
              </p>

              <div className="partner-revenue-scale">
                <p className="partner-revenue-scale-label">REFERRAL VALUE OVER TIME</p>
                <div className="partner-revenue-scale-bar" aria-hidden="true">
                  <span />
                  <span />
                  <span />
                  <span />
                  <span />
                  <span />
                  <span />
                  <span />
                  <span />
                  <span />
                </div>
                <div className="partner-revenue-scale-foot">
                  <p>Early stage: referral starts</p>
                  <p>Later stage: long-term value</p>
                </div>
              </div>

              <div className="partner-revenue-panel">
                <p className="primary partner-revenue-panel-intro">
                  When a referred client starts care and remains with edyn.care, your agency can
                  benefit from both immediate referral income and ongoing collaborative-care revenue.
                </p>

                <div className="partner-revenue-grid">
                  <div className="partner-revenue-item">
                    <h3>Upfront referral fee</h3>
                    <p className="value">£800</p>
                    <p className="primary">
                      Paid when a client you refer starts care and remains with edyn.care for 4 weeks.
                    </p>
                  </div>
                  <div className="partner-revenue-item">
                    <h3>Collaborative care</h3>
                    <p className="value">2% weekly</p>
                    <p className="primary">
                      If your agency continues supporting through double-up care, respite, or additional
                      visits.
                    </p>
                  </div>
                  <div className="partner-revenue-item">
                    <h3>Typical total outcome</h3>
                    <p className="value">~£1,296</p>
                    <p className="primary">
                      Based on a £1,417 average weekly package and 320 day average client duration.
                    </p>
                  </div>
                </div>
              </div>
              <p className="primary partner-revenue-footnote">
                Based on average package duration and cost. Additional services such as driver support
                or food allowances are not included.
              </p>
            </div>
          </section>

          <section>
            <div className="locations-livein-goto partner-how-steps">
              <div className="goto-left">
                <h1>
                  How the Partnership
                  <br />
                  Works
                </h1>
              </div>
              <div className="goto-right">
                <div className="goto-reason">
                  <div className="partner-icon-placeholder">1</div>
                  <div className="goto-content">
                    <h3>Refer a Client</h3>
                    <p className="primary">
                      When a client&apos;s care needs increase beyond hourly care, refer them to edyn.care.
                    </p>
                  </div>
                </div>
                <div className="goto-reason">
                  <div className="partner-icon-placeholder">2</div>
                  <div className="goto-content">
                    <h3>We Assess and Arrange Care</h3>
                    <p className="primary">Our team manages the consultation, matching and onboarding process.</p>
                  </div>
                </div>
                <div className="goto-reason">
                  <div className="partner-icon-placeholder">3</div>
                  <div className="goto-content">
                    <h3>The Client Receives Live-in Care at Home</h3>
                    <p className="primary">Care begins and the client remains safely in their own home.</p>
                  </div>
                </div>
                <div className="goto-reason">
                  <div className="partner-icon-placeholder">4</div>
                  <div className="goto-content">
                    <h3>Your Agency Receives Referral Income</h3>
                    <p className="primary">
                      Care begins and the client remains safely in their own home. You receive £800 referral fee + % of package revenue.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section>
            <Trustpilot className="partner-trustpilot" />
          </section>

          <section className="partner-typeform" id="partner-typeform">
            <h1>Join the edyn.care Care Agency Network</h1>
            <p className="primary">
              We are currently building partnerships with hourly care providers across the UK.
              If you&apos;re interested in working together, register your agency below.
            </p>
            <div className="partner-typeform-frame-wrap">
              <iframe
                title="Register your agency interest"
                src="https://edyncare.typeform.com/to/lDhGUk01"
                frameBorder="0"
                allow="camera; microphone; autoplay; encrypted-media;"
              />
            </div>
          </section>
        </main>
        <Footer includeCTA={false} />
      </div>
    </>
  );
}
